/**
 * DEFUSE S15 — typed property descriptors for the legacy heavy `POLYLINE`,
 * `ACAD_TABLE` and `OLE2FRAME` kinds. Special (non-common) fields only; the
 * shared object properties come from `buildCommonDescriptors`.
 *
 * # What is editable here, and what deliberately is not
 *
 * Editable rows are the ones with a real typed home AND a real inverse: the
 * polyline's two group-70 booleans and its default widths / elevation, the
 * table's insertion point, the OLE frame's corners.
 *
 * Those rows are WIRED, not merely declared (@codex F1, P1 — they were declared
 * before the routing existed): all three kinds are in
 * `TYPED_EXTENDED_GEOMETRY_KINDS`, so a `properties/apply` reaches them through
 * the ONE authoritative contour — dispatch → geometryReducer Step 1d →
 * `buildTypedExtendedGeometryEdit` → `EntityMutation` → `commitMutation` — with
 * the shell preview gated by the same descriptors
 * (`extendedPatchableLookup` + `typedExtendedGeometryShellRejects`) and the undo
 * inverse carrying raw document precision (`rawGeometryPropertyValues`). Adding
 * an editable descriptor to this module is therefore a THREE-part change: the
 * descriptor, the kind's owned-label routing, and the patchable/persistable
 * advertisement — a row wired in only the first is a placeholder.
 *
 * Read-only rows are read-only because EDITING them is a different feature,
 * not because the value was hard to plumb:
 *
 *   - polyline `Vertices`, mesh counts, smooth densities and surface type —
 *     restructuring a heavy polyline's vertex list or mesh grid is PEDIT /
 *     grip territory (and for a mesh, regenerates the whole M×N vertex grid);
 *   - every table row but the position — cell values, formulas, cell styles,
 *     merges, TABLESTYLE management and table breaking are the deferred
 *     table-editing feature (see `tableEntitySchema`'s boundary doc);
 *   - the OLE record's descriptors — they describe an embedded object this
 *     project does not decode.
 *
 * Advertising an editable row with no typed write behind it is exactly the
 * placeholder CLAUDE.md ПРИОРИТЕТ #1 forbids, so those rows are honestly
 * typed `read-only-calc` / `ui-projection-deferred` instead.
 */

import {
  DXF_POLYLINE_FLAG,
  polylineSubtype,
  tableRotationDegrees,
  type OleFrameEntity,
  type PolylineEntity,
  type TableEntity,
} from "@dxf-ai-editor/drawing-model";

import { formatNumber } from "../geometryEntityTypes";
import { heavyPolylinePathMetrics } from "../heavyPolylinePathMetrics";
import {
  calc,
  coord,
  deferred,
  make,
  num,
  type TypedPropertyDescriptor,
} from "./types";

const XY_PRESERVE = { zPreserve: true, docPrecisionReconcile: true } as const;

/* ── POLYLINE (legacy heavy) ───────────────────────────────────────────── */

/** AutoCAD's Properties palette header for each group-70 subtype. */
const POLYLINE_TYPE_LABEL: Record<
  ReturnType<typeof polylineSubtype>,
  string
> = {
  "2d": "2D Polyline",
  "3d": "3D Polyline",
  "polygon-mesh": "Polygon Mesh",
  "polyface-mesh": "Polyface Mesh",
};

/** DXF group 75 — curves and smooth-surface type, verbatim from the reference. */
const SURFACE_TYPE_LABEL: Record<number, string> = {
  0: "None",
  5: "Quadratic B-spline",
  6: "Cubic B-spline",
  8: "Bezier",
};

/**
 * The 2D projection of the vertex list the display row serializes, in the
 * same `x,y;x,y` form `dxfGeometry` uses for every other polyline family.
 */
function serializeVertices(entity: PolylineEntity): string {
  return entity.vertices
    .map((v) => `${formatNumber(v.location.x)},${formatNumber(v.location.y)}`)
    .join(";");
}

/**
 * A group-70 BIT toggle. `read` returns the WHOLE flags integer (the real
 * typed home — a bitfield has no per-bit field), `display` projects the one
 * bit, and `toRaw` folds the edited bit back into the current integer so the
 * other seven bits survive. Round-trips by construction: display is derived
 * from the same bit `toRaw` writes.
 */
function flagBit(
  label: string,
  bit: number,
): TypedPropertyDescriptor {
  return make(
    label,
    "boolean",
    { path: "flags", read: (e) => (e as PolylineEntity).flags },
    {
      display: (e) =>
        ((e as PolylineEntity).flags & bit) !== 0 ? "true" : "false",
      toRaw: (s, entity) => {
        const current = (entity as PolylineEntity | undefined)?.flags ?? 0;
        const on = ["yes", "true", "closed", "on", "1"].includes(
          s.trim().toLowerCase(),
        );
        return on ? current | bit : current & ~bit;
      },
    },
  );
}

/**
 * One of the two entity-level DEFAULT width rows (DXF groups 40/41), whose typed
 * home is OPTIONAL. `display` projects an absent group as the inherited `0` so
 * the descriptor, both preview factories and AutoCAD's palette agree on one
 * string — see the call sites' note for why that agreement is what protects the
 * optional-presence bit from a full-bag re-submit.
 */
function widthDefault(
  label: string,
  path: string,
  read: (e: PolylineEntity) => number | undefined,
): TypedPropertyDescriptor {
  return {
    ...num<PolylineEntity>(label, path, read, { min: 0 }),
    display: (e) => formatNumber(read(e as PolylineEntity) ?? 0),
  };
}

export const polylineDescriptors: TypedPropertyDescriptor[] = [
  // The palette header AutoCAD shows for this polyline's group-70 subtype.
  calc<PolylineEntity>(
    "Polyline type",
    (e) => POLYLINE_TYPE_LABEL[polylineSubtype(e)],
  ),
  // The vertex list, read-only: restructuring a heavy polyline's vertices (or
  // a mesh's M×N grid) is PEDIT / grip territory, not a Properties edit.
  calc<PolylineEntity>("Vertices", (e) => serializeVertices(e), (e) =>
    serializeVertices(e),
  ),
  // Group 70 bit 1 (closed / closed-in-M) and bit 128 (plinegen). Same
  // "true"/"false" wire form LWPOLYLINE's own two rows use, so a MATCHPROP
  // copy between the two polyline families reads one convention.
  flagBit("Closed", DXF_POLYLINE_FLAG.CLOSED),
  flagBit("Linetype generation", DXF_POLYLINE_FLAG.CONTINUOUS_LINETYPE),
  // Groups 40/41 — the entity-level DEFAULT widths every vertex without its
  // own 40/41 inherits. Kept as the two separate DXF values.
  //
  // Both groups are OPTIONAL (absent ⇒ every vertex inherits 0), so the display
  // projects the INHERITED 0 — the same `?? 0` projection the production
  // single-select builder emits (`dxfGeometry.buildPolylinePreview`), which is
  // what AutoCAD shows too. That agreement is load-bearing now that the label is
  // typed-writer-owned (@codex F1): the Properties palette re-submits its WHOLE
  // row set on every apply, and the writer's display-equality no-op
  // (`isTypedFieldEditNoop`) is what keeps a stale unchanged "0" row from turning
  // an ABSENT group 40/41 into an explicitly-written 0 — the presence-bit
  // distinction the import path deliberately preserves. A GENUINE width edit
  // still applies and writes the group.
  widthDefault("Start segment width", "defaultStartWidth", (e) => e.defaultStartWidth),
  widthDefault("End segment width", "defaultEndWidth", (e) => e.defaultEndWidth),
  // Group 10/20/30 Z — the "dummy point"'s Z IS the elevation per the DXF
  // reference; X and Y are structurally always 0 and are not surfaced.
  num<PolylineEntity>("Elevation", "elevation.z", (e) => e.elevation.z),
  // Length / Area — PATH metrics, so they exist only for a subtype that HAS a
  // path. The whole rule (which VERTEX records form the visible run, in what
  // order, and which subtypes have a path at all) lives in ONE place
  // (`../heavyPolylinePathMetrics.ts`, over cad-core's `heavyPolylineRuns`), so
  // this table and the apps/web single-select preview bag
  // (`dxfGeometry.ts::buildPolylinePreview`) cannot answer it differently.
  //
  // A polygon or poly-face mesh resolves to `undefined` for the PAIR and both
  // rows render EMPTY (`resolveDisplay`'s `defaultFormat`), exactly as
  // AutoCAD's palette shows no Length and no Area row for `AcDbPolygonMesh` /
  // `AcDbPolyFaceMesh`. A 3D polyline resolves to a Length and an `undefined`
  // AREA — the palette shows Length for `AcDb3dPolyline` and no Area row,
  // because a 3D polyline's closing polygon is in general non-planar and the
  // XY shoelace would be its SHADOW's area, not the entity's (@codex PR #785
  // round 6). A `0` in either row would read as a measured value; the empty
  // cell is the same visible "not applicable to this subtype" projection the
  // mesh rows below already use in the other direction on a plain polyline.
  // See that module's header for the full AutoCAD reference (@codex PR #785
  // round 5, FINDING B).
  calc<PolylineEntity>("Length", (e) => heavyPolylinePathMetrics(e)?.length),
  calc<PolylineEntity>("Area", (e) => heavyPolylinePathMetrics(e)?.area),
  // Groups 71–75 — mesh structure. Read-only: changing a mesh's M/N counts
  // regenerates its whole vertex grid, which AutoCAD does through PEDIT /
  // MESHSMOOTH, never through a Properties cell.
  calc<PolylineEntity>("M vertex count", (e) => e.meshMCount),
  calc<PolylineEntity>("N vertex count", (e) => e.meshNCount),
  calc<PolylineEntity>("Smooth surface M density", (e) => e.smoothSurfaceMDensity),
  calc<PolylineEntity>("Smooth surface N density", (e) => e.smoothSurfaceNDensity),
  calc<PolylineEntity>("Surface type", (e) =>
    e.surfaceType === undefined
      ? undefined
      : (SURFACE_TYPE_LABEL[e.surfaceType] ?? String(e.surfaceType)),
  ),
];

/* ── ACAD_TABLE ────────────────────────────────────────────────────────── */

export const tableDescriptors: TypedPropertyDescriptor[] = [
  // The style NAME lives on the TABLESTYLE object in the OBJECTS section, not
  // on the entity (which names it by handle, group 342). Descriptors resolve
  // against a lone `DrawingEntity` and have no access to the document's
  // retained object groups, so this row is a UI projection sourced from the
  // preview bag (`resolveTableStyleName`) — the same shape the INSERT's
  // BLOCK_RECORD-sourced rows use, and listed in SINGLE_SELECT_BAG_RESIDUAL
  // for the same reason. It is NEVER a hard-coded "Standard".
  deferred("Table style"),
  // Row / column structure. Read-only: inserting or deleting rows and columns
  // rewrites the cell records this change deliberately retains verbatim (see
  // `tableEntitySchema`'s boundary doc).
  calc<TableEntity>("Rows", (e) => e.rowCount),
  calc<TableEntity>("Columns", (e) => e.columnCount),
  calc<TableEntity>("Table width", (e) =>
    e.columnWidths.reduce((sum, w) => sum + w, 0),
  ),
  calc<TableEntity>("Table height", (e) =>
    e.rowHeights.reduce((sum, h) => sum + h, 0),
  ),
  // The insertion point IS editable — moving a table is supported (the same
  // `transformEntityCoords` arm MOVE / COPY / ROTATE use).
  coord<TableEntity>("Position X", "insertionPoint.x", (e) => e.insertionPoint.x, XY_PRESERVE),
  coord<TableEntity>("Position Y", "insertionPoint.y", (e) => e.insertionPoint.y, XY_PRESERVE),
  coord<TableEntity>("Position Z", "insertionPoint.z", (e) => e.insertionPoint.z, XY_PRESERVE),
  // AcDbTable stores orientation as the horizontal-direction VECTOR (group
  // 11/21/31), not an angle; the degrees shown here are derived from it
  // through the one shared `tableRotationDegrees`. Read-only because writing
  // it back means re-authoring that vector, which belongs with the table
  // editing feature.
  calc<TableEntity>("Rotation", (e) => tableRotationDegrees(e)),
];

/* ── OLE2FRAME ─────────────────────────────────────────────────────────── */

/** DXF group 71 — OLE object type. */
const OLE_OBJECT_TYPE_LABEL: Record<number, string> = {
  1: "Link",
  2: "Embedded",
  3: "Static",
};

/**
 * DXF group 73 — OLE draw aspect. Present in AutoCAD-authored files but
 * absent from the published group-code table, so an unrecognised value is
 * shown verbatim rather than mapped to a guess.
 */
const OLE_DRAW_ASPECT_LABEL: Record<number, string> = {
  1: "Content",
  2: "Thumbnail",
  3: "Icon",
  4: "Doc print",
};

export const oleFrameDescriptors: TypedPropertyDescriptor[] = [
  calc<OleFrameEntity>("OLE object type", (e) =>
    e.oleObjectType === undefined
      ? e.objectDescription
      : (OLE_OBJECT_TYPE_LABEL[e.oleObjectType] ?? String(e.oleObjectType)),
  ),
  calc<OleFrameEntity>("OLE version", (e) => e.oleVersion),
  calc<OleFrameEntity>("Draw aspect", (e) =>
    e.drawAspect === undefined
      ? undefined
      : (OLE_DRAW_ASPECT_LABEL[e.drawAspect] ?? String(e.drawAspect)),
  ),
  // The frame corners ARE editable — resizing / repositioning an OLE frame is
  // exactly what AutoCAD's corner grips do, and the frame is the entity's
  // whole geometry.
  coord<OleFrameEntity>("Upper left X", "upperLeft.x", (e) => e.upperLeft.x, XY_PRESERVE),
  coord<OleFrameEntity>("Upper left Y", "upperLeft.y", (e) => e.upperLeft.y, XY_PRESERVE),
  coord<OleFrameEntity>("Lower right X", "lowerRight.x", (e) => e.lowerRight.x, XY_PRESERVE),
  coord<OleFrameEntity>("Lower right Y", "lowerRight.y", (e) => e.lowerRight.y, XY_PRESERVE),
  calc<OleFrameEntity>("Width", (e) => Math.abs(e.lowerRight.x - e.upperLeft.x)),
  calc<OleFrameEntity>("Height", (e) => Math.abs(e.upperLeft.y - e.lowerRight.y)),
  // Group 90 — the retained payload's byte count. The payload itself is never
  // decoded (see `oleFrameEntitySchema`'s doc), so its size is the only thing
  // about it this project can honestly report.
  calc<OleFrameEntity>("Data size", (e) => e.binaryByteCount),
];
